import type { FilterResult } from '../../services/filter';
/**
 * Filters the data using the Inverse Chebyshev filtering concept.
 *
 * This uses direct multiplication instead of Math.pow.
 * Doing direct multiplication proves to be faster than Math.pow
 *
 * @module
 */
export default class InverseChebyshevFilter {
  /**
   * Filters the data
   * @function
   * @param {float} frequency
   * @param {float} fc
   * @param {int} filterOrder
   * @param {float} stopbandAtten
   * @returns {FilterResult}
   */
  filterOne(frequency: number, fc: number, filterOrder: number, stopbandAtten: number): FilterResult {
    const er = 1 / Math.sqrt(Math.pow(10, stopbandAtten / 10) - 1),
      a = this.acosh(1 / er) / filterOrder,
      fo = fc * this.cosh(a),
      x = fo / frequency;
    let t = 0;

    switch (filterOrder) {
      case 1: {
        t = x;
        break;
      }
      case 2: {
        t = 2 * x * x - 1;
        break;
      }
      case 3: {
        t = 4 * x * x * x - 3 * x;
        break;
      }
      case 4: {
        t = 8 * x * x * x * x - 8 * x * x + 1;
        break;
      }
      case 5: {
        t = 16 * x * x * x * x * x - 20 * x * x * x + 5 * x;
        break;
      }
      case 6: {
        t = 32 * x * x * x * x * x * x - 48 * x * x * x * x + 18 * x * x - 1;
        break;
      }
      case 7: {
        t = 64 * x * x * x * x * x * x * x - 112 * x * x * x * x * x + 56 * x * x * x - 7 * x;
        break;
      }
    }

    const numerator = 1,
      denominator = 1 + 1 / (er * er * t * t),
      ysquared = numerator / denominator,
      y = Math.abs(Math.sqrt(ysquared)),
      z = (20 * Math.log(y)) / Math.log(10);

    return { filterAmplitude: z, filterAttenuation: y };
  }

  /**
   * discuss at: http://phpjs.org/functions/acosh/
   *
   * original by: Onno Marsman
   *
   * @param {Number} arg
   *
   * @example
   * // returns 16.674657798418625
   *    acosh(8723321.4)
   *
   * @private
   */
  acosh(arg: number): number {
    let result = 0;
    if (!Math.acosh) {
      result = Math.log(arg + Math.sqrt(arg * arg - 1));
    } else {
      result = Math.acosh(arg);
    }

    return result;
  }

  /**
   * discuss at: http://phpjs.org/functions/cosh/
   *
   * original by: Onno Marsman
   *
   * @param {Number} arg
   *
   * @example
   * // returns 1.0164747716114113
   *    cosh(-0.18127180117607017);
   *
   * @private
   */
  cosh(arg: number): number {
    let result = 0;
    if (!Math.cosh) {
      result = (Math.exp(arg) + Math.exp(-arg)) / 2;
    } else {
      result = Math.cosh(arg);
    }

    return result;
  }
}
